import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';
import type {
  CellEditRequestEvent,
  FilterChangedEvent,
  IRowNode,
  RowClassParams,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import type { AgGridReactProps } from 'ag-grid-react';
import clsx from 'clsx';
import { Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WithLocalStorage } from '@/components';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef } from '@/grid/defaults';
import { getRowId } from '@/grid/types';
import { useAccessToken } from '@/services/auth';
import { PrimaryContext } from '@/services/context';
import {
  isOnFarmFreezingSelectionEligible,
  type OnFarmImplantApiSourceType,
  type OnFarmImplantQueueEditableField,
  type OnFarmImplantQueueFieldChanges,
  type OnFarmImplantQueueFieldValue,
  onFarmImplantQueueOptions,
  type OnFarmImplantQueueRecord,
  OnFarmImplantRequestError,
  useUpdateOnFarmImplantQueueFields,
} from '@/services/on-farm-implant';
import { useContextMenu } from '@/utils/context-menu';

import { SendToFreezingDialog } from '../OnFarmFreezingTab/SendToFreezingDialog';
import { AssignBarcodeIdsDialog } from './AssignBarcodeIdsDialog';
import {
  IMPLANT_QUEUE_EDITABLE_FIELDS,
  IMPLANT_QUEUE_SOURCE_TYPES,
  useImplantQueueColumnDefs,
  useSourceTypeLabel,
} from './columnDefs';
import { DiscardQueueEmbryoDialog } from './DiscardQueueEmbryoDialog';
import { MassChangeDialog } from './MassChangeDialog';
import { normalizeQueueCellValue } from './normalizeQueueCellValue';
import { useQueueEditHistory } from './useQueueEditHistory';

const ALL_SOURCES = 'all';

type SourceFilter = OnFarmImplantApiSourceType | typeof ALL_SOURCES;

type ImplantQueueTabProps = {
  sessionId: string | null;
  readOnly?: boolean;
};

const EDITABLE_FIELD_SET: ReadonlySet<string> = new Set(
  IMPLANT_QUEUE_EDITABLE_FIELDS,
);

const isIncomplete = (row: OnFarmImplantQueueRecord | undefined) =>
  !!row?.missingFields?.length;

export const ImplantQueueTab = ({
  sessionId,
  readOnly = false,
}: ImplantQueueTabProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const gridOptions = useDefaultGridOptions();
  const { themeMode } = useContext(PrimaryContext);
  const { getContextMenuItems } = useContextMenu({ idKey: 'sessionQueueId' });
  const gridRef = useRef<AgGridReact>(null);
  const sourceTypeLabel = useSourceTypeLabel();

  const [searchText, setSearchText] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>(ALL_SOURCES);
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const incompleteOnlyRef = useRef(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [massChangeOpen, setMassChangeOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [freezingOpen, setFreezingOpen] = useState(false);
  const [barcodeRows, setBarcodeRows] = useState<OnFarmImplantQueueRecord[]>(
    [],
  );
  const [barcodeOpen, setBarcodeOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useQuery({
    ...onFarmImplantQueueOptions(token, { sessionId: sessionId ?? '' }),
    enabled: !!token && !!sessionId,
  });

  const { mutateAsync: updateFields } = useUpdateOnFarmImplantQueueFields();
  const { history, record, clear } = useQueueEditHistory();

  const showSaveError = useCallback(
    (error: unknown) => {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        toast.add({
          data: { variant: 'error' },
          title: t('onFarmImplant.implantQueue.saveFailed'),
          description: error.problems.join(' '),
        });
        return;
      }
      console.error('Failed to save implant queue field:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.implantQueue.saveFailed'),
      });
    },
    [t],
  );

  const saveField = useCallback(
    async (
      row: OnFarmImplantQueueRecord,
      field: OnFarmImplantQueueEditableField,
      value: OnFarmImplantQueueFieldValue,
    ) => {
      if (!sessionId) return false;
      try {
        await updateFields({
          sessionId,
          input: {
            sessionQueueIds: [row.sessionQueueId],
            changes: { [field]: { value } },
            versions: row.versions,
          },
        });
        return true;
      } catch (error) {
        showSaveError(error);
        return false;
      }
    },
    [sessionId, showSaveError, updateFields],
  );

  const applyFieldChange = useCallback(
    async (
      row: OnFarmImplantQueueRecord,
      field: OnFarmImplantQueueEditableField,
      oldValue: unknown,
      newValue: unknown,
    ) => {
      const previous = normalizeQueueCellValue(field, oldValue);
      const next = normalizeQueueCellValue(field, newValue);
      if (previous === next) return;

      const saved = await saveField(row, field, next);
      if (saved) {
        record([{ rowId: row.sessionQueueId, field, previous }]);
      }
    },
    [record, saveField],
  );

  const handleCellEditRequest = useCallback(
    async (event: CellEditRequestEvent) => {
      const row = event.data as OnFarmImplantQueueRecord | undefined;
      const field = event.colDef.field;
      if (!row || !field || !EDITABLE_FIELD_SET.has(field)) return;
      await applyFieldChange(
        row,
        field as OnFarmImplantQueueEditableField,
        event.oldValue,
        event.newValue,
      );
    },
    [applyFieldChange],
  );

  const handleFieldChange = useCallback(
    (
      row: OnFarmImplantQueueRecord,
      field: OnFarmImplantQueueEditableField,
      value: OnFarmImplantQueueFieldValue,
    ) => {
      void applyFieldChange(row, field, row[field], value);
    },
    [applyFieldChange],
  );

  const handleRevert = useCallback(
    async (
      row: OnFarmImplantQueueRecord,
      field: OnFarmImplantQueueEditableField,
      previous: OnFarmImplantQueueFieldValue,
    ) => {
      const saved = await saveField(row, field, previous);
      if (saved) clear(row.sessionQueueId, field);
    },
    [clear, saveField],
  );

  const handleMassChangeApplied = useCallback(
    (
      changedRows: OnFarmImplantQueueRecord[],
      changes: OnFarmImplantQueueFieldChanges,
    ) => {
      const fields = Object.keys(changes) as OnFarmImplantQueueEditableField[];
      record(
        changedRows.flatMap((row) =>
          fields
            .map((field) => ({
              rowId: row.sessionQueueId,
              field,
              previous: normalizeQueueCellValue(field, row[field]),
            }))
            .filter(
              (entry) =>
                entry.previous !== (changes[entry.field]?.value ?? null),
            ),
        ),
      );
    },
    [record],
  );

  const handleBarcodeIdsApplied = useCallback(
    (numberedRows: OnFarmImplantQueueRecord[], startingBarcodeId: number) => {
      record(
        numberedRows.flatMap((row, index) => {
          const previous = normalizeQueueCellValue(
            'dairyCompId',
            row.dairyCompId,
          );
          return previous === startingBarcodeId + index
            ? []
            : [{ rowId: row.sessionQueueId, field: 'dairyCompId', previous }];
        }),
      );
    },
    [record],
  );

  const columnDefs = useImplantQueueColumnDefs({
    readOnly,
    history,
    onRevert: handleRevert,
    onFieldChange: handleFieldChange,
  });

  useEffect(() => {
    gridRef.current?.api?.refreshCells({
      columns: IMPLANT_QUEUE_EDITABLE_FIELDS,
      force: true,
    });
  }, [history]);

  const rows = useMemo(() => data?.items ?? [], [data]);

  const incompleteCount = useMemo(
    () => rows.filter(isIncomplete).length,
    [rows],
  );

  const selectedRows = useMemo(() => {
    const ids = new Set(selectedIds);
    return rows.filter((row) => ids.has(row.sessionQueueId));
  }, [rows, selectedIds]);

  const canSendToFreezing = useMemo(
    () => isOnFarmFreezingSelectionEligible(selectedRows),
    [selectedRows],
  );

  const freezingEmbryos = useMemo(
    () =>
      selectedRows.map((row) => ({
        sessionEmbryoId: row.sessionEmbryoId,
        embryoName: row.embryoName,
        strawNumber: row.transferStrawNumber,
        stage: row.stage,
        grade: row.grade,
        versions: row.versions,
      })),
    [selectedRows],
  );

  const countsBySourceType = useMemo(() => {
    const counts = new Map<OnFarmImplantApiSourceType, number>();
    for (const row of rows) {
      counts.set(row.sourceType, (counts.get(row.sourceType) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const availableSourceTypes = useMemo(
    () =>
      IMPLANT_QUEUE_SOURCE_TYPES.filter((sourceType) =>
        countsBySourceType.has(sourceType),
      ),
    [countsBySourceType],
  );

  const handleSelectionChanged = useCallback((event: SelectionChangedEvent) => {
    setSelectedIds(
      (event.api.getSelectedRows() as OnFarmImplantQueueRecord[]).map(
        (row) => row.sessionQueueId,
      ),
    );
  }, []);

  const handleClearSelection = useCallback(() => {
    gridRef.current?.api.deselectAll();
  }, []);

  const handleOpenBarcodeIds = useCallback(() => {
    const byId = new Map(
      selectedRows.map((row) => [row.sessionQueueId, row] as const),
    );
    const ordered: OnFarmImplantQueueRecord[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      const id = (node.data as OnFarmImplantQueueRecord | undefined)
        ?.sessionQueueId;
      const row = id ? byId.get(id) : undefined;
      if (!row) return;
      ordered.push(row);
      byId.delete(row.sessionQueueId);
    });
    const hidden = [...byId.values()].sort(
      (a, b) => (a.queueOrder ?? 0) - (b.queueOrder ?? 0),
    );
    setBarcodeRows([...ordered, ...hidden]);
    setBarcodeOpen(true);
  }, [selectedRows]);

  const isRowSelectable = useCallback(
    (node: IRowNode) =>
      !readOnly &&
      !!(node.data as OnFarmImplantQueueRecord | undefined)?.isEditable,
    [readOnly],
  );

  const getRowClass = useCallback(
    (params: RowClassParams) =>
      params.data && !(params.data as OnFarmImplantQueueRecord).isEditable
        ? 'opacity-60'
        : undefined,
    [],
  );

  const isExternalFilterPresent = useCallback(
    () => incompleteOnlyRef.current,
    [],
  );

  const doesExternalFilterPass = useCallback(
    (node: IRowNode) =>
      isIncomplete(node.data as OnFarmImplantQueueRecord | undefined),
    [],
  );

  const handleIncompleteToggle = useCallback(() => {
    const next = !incompleteOnlyRef.current;
    incompleteOnlyRef.current = next;
    setIncompleteOnly(next);
    gridRef.current?.api.onFilterChanged();
  }, []);

  const handleSourceFilterChange = useCallback(
    (nextFilter: SourceFilter) => {
      setSourceFilter(nextFilter);

      const api = gridRef.current?.api;
      if (!api) return;

      const model = { ...api.getFilterModel() };

      if (nextFilter === ALL_SOURCES) {
        delete model.sourceType;
      } else {
        model.sourceType = {
          filterType: 'set',
          values: [sourceTypeLabel(nextFilter)],
        };
      }

      api.setFilterModel(model);
    },
    [sourceTypeLabel],
  );

  const handleFilterChanged = useCallback(
    (event: FilterChangedEvent) => {
      const model = event.api.getFilterModel().sourceType as
        | { values?: string[] }
        | undefined;
      const values = model?.values;

      if (!values || values.length !== 1) {
        setSourceFilter(ALL_SOURCES);
        return;
      }

      const matched = IMPLANT_QUEUE_SOURCE_TYPES.find(
        (sourceType) => sourceTypeLabel(sourceType) === values[0],
      );

      setSourceFilter(matched ?? ALL_SOURCES);
    },
    [sourceTypeLabel],
  );
  if (!sessionId) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-clock mb-3 text-4xl" />
        <p className="text-sm">{t('onFarmImplant.implantQueue.noSession')}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.implantQueue.loading')}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.implantQueue.loadError')}</p>
        <Button
          onClick={() => {
            void refetch();
          }}
        >
          {t('onFarmImplant.implantQueue.retry')}
        </Button>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
        <i className="pi pi-clock mb-3 text-4xl" />
        <p className="text-base font-medium">
          {t('onFarmImplant.implantQueue.emptyTitle')}
        </p>
        <p className="mt-1 text-sm">
          {t('onFarmImplant.implantQueue.emptyBody')}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col px-4 py-3">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="relative w-56">
            <i className="pi pi-search absolute top-1/2 left-2 -translate-y-1/2 text-xs text-gray-400" />
            <Input
              placeholder={t('onFarmImplant.implantQueue.filterPlaceholder')}
              className="h-7 pl-7 text-xs"
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
            />
          </div>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.implantQueue.queueCount', {
              count: data?.totalRecords ?? rows.length,
            })}
          </span>
          {!readOnly && (
            <>
              <span className="text-xs text-gray-500">
                {t('onFarmImplant.implantQueue.selectedCount', {
                  count: selectedRows.length,
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={selectedRows.length === 0}
                onClick={handleClearSelection}
              >
                {t('onFarmImplant.implantQueue.clearSelection')}
              </Button>
              <Button
                size="sm"
                disabled={selectedRows.length === 0}
                onClick={() => setMassChangeOpen(true)}
              >
                <Pencil />
                {t('onFarmImplant.implantQueue.massChange.button', {
                  count: selectedRows.length,
                })}
              </Button>
              <Button
                size="sm"
                disabled={selectedRows.length === 0}
                onClick={handleOpenBarcodeIds}
              >
                {t('onFarmImplant.implantQueue.assignBarcodeIds.button')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!canSendToFreezing}
                title={
                  selectedRows.length > 0 && !canSendToFreezing
                    ? t('onFarmImplant.onFarmFreezing.send.ineligibleHint')
                    : undefined
                }
                onClick={() => setFreezingOpen(true)}
              >
                {t('onFarmImplant.onFarmFreezing.send.button')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                disabled={selectedRows.length === 0}
                onClick={() => setDiscardOpen(true)}
              >
                {t('onFarmImplant.implantQueue.discard.action')}
              </Button>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-pressed={incompleteOnly}
            onClick={handleIncompleteToggle}
            className={clsx(
              'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium',
              incompleteOnly
                ? 'border-amber-500 bg-amber-500 text-white'
                : 'border-amber-300 bg-white text-amber-700',
            )}
          >
            <i className="pi pi-exclamation-triangle text-[10px]" />
            {t('onFarmImplant.implantQueue.incompleteOnly', {
              count: incompleteCount,
            })}
          </button>
          <span className="text-xs text-gray-500">
            {t('onFarmImplant.implantQueue.sourceFilterLabel')}
          </span>
          <button
            type="button"
            aria-pressed={sourceFilter === ALL_SOURCES}
            onClick={() => handleSourceFilterChange(ALL_SOURCES)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium',
              sourceFilter === ALL_SOURCES
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-gray-300 bg-white text-gray-700',
            )}
          >
            {t('onFarmImplant.implantQueue.allSources')}
          </button>
          {availableSourceTypes.map((sourceType) => (
            <button
              key={sourceType}
              type="button"
              aria-pressed={sourceFilter === sourceType}
              onClick={() => handleSourceFilterChange(sourceType)}
              className={clsx(
                'rounded-full border px-3 py-1 text-xs font-medium',
                sourceFilter === sourceType
                  ? 'border-blue-600 bg-blue-600 text-white'
                  : 'border-gray-300 bg-white text-gray-700',
              )}
            >
              {sourceTypeLabel(sourceType)} (
              {countsBySourceType.get(sourceType) ?? 0})
            </button>
          ))}
        </div>
      </div>

      <div
        className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''} h-[calc(100vh-13rem)] min-h-[400px] w-full`}
      >
        <WithLocalStorage storageKey="on-farm-implant-implant-queue">
          <AgGridReact
            ref={gridRef}
            gridOptions={gridOptions}
            columnDefs={columnDefs as AgGridReactProps['columnDefs']}
            maintainColumnOrder
            defaultColDef={defaultColDef}
            rowData={rows}
            headerHeight={36}
            rowHeight={48}
            quickFilterText={searchText}
            getRowId={getRowId<OnFarmImplantQueueRecord>(
              (params) => params.data.sessionQueueId,
            )}
            getContextMenuItems={getContextMenuItems}
            onFilterChanged={handleFilterChanged}
            rowSelection="multiple"
            suppressRowClickSelection
            isRowSelectable={isRowSelectable}
            getRowClass={getRowClass}
            onSelectionChanged={handleSelectionChanged}
            readOnlyEdit
            onCellEditRequest={handleCellEditRequest}
            isExternalFilterPresent={isExternalFilterPresent}
            doesExternalFilterPass={doesExternalFilterPass}
            stopEditingWhenCellsLoseFocus
            enterNavigatesVertically
            enterNavigatesVerticallyAfterEdit
          />
        </WithLocalStorage>
      </div>

      {sessionId && (
        <MassChangeDialog
          open={massChangeOpen}
          onOpenChange={setMassChangeOpen}
          sessionId={sessionId}
          rows={selectedRows}
          onApplied={handleMassChangeApplied}
        />
      )}

      {sessionId && (
        <AssignBarcodeIdsDialog
          open={barcodeOpen}
          onOpenChange={setBarcodeOpen}
          sessionId={sessionId}
          rows={barcodeRows}
          onApplied={handleBarcodeIdsApplied}
        />
      )}

      {sessionId && (
        <DiscardQueueEmbryoDialog
          open={discardOpen}
          onOpenChange={setDiscardOpen}
          sessionId={sessionId}
          embryos={selectedRows}
          onDiscarded={handleClearSelection}
        />
      )}

      {sessionId && (
        <SendToFreezingDialog
          open={freezingOpen}
          onOpenChange={setFreezingOpen}
          sessionId={sessionId}
          embryos={freezingEmbryos}
          fromQueue
          onSent={handleClearSelection}
        />
      )}
    </div>
  );
};
